import { gql } from '@apollo/client';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import React, { useEffect, useState } from 'react';

import {
  CartFragment,
  createCart,
  useCheckoutEffect,
} from '../graphql/checkoutQuery';

import useCartStore from '../stores/cartStore';

const GET_CART = gql`
  query GetCart($cartId: ID!) {
    cart(id: $cartId) {
      ...CartFragment
    }
  }
  ${CartFragment}
`;

export const formatCustomAttributes = (params) => {
  return Object.entries(params).map(([k,v]) => { return { key: k, value: v } });
};

export const extractCustomAttributes = (attributes) => {
  const attrObj = {};
  attributes.forEach((attribute) => {
    attrObj[attribute.key] = attribute.value;
  });
  return attrObj;
};

const CartContext = React.createContext([{}, () => {}]);
const CheckoutContext = CartContext;

const CartProvider = (props) => {
  const client = useApolloClient();
  const [cart, setCart] = useState({ lines: { edges: [] }});
  const [hasHydratedCartStore, setHasHydratedCartStore] = useState(
    () => useCartStore.persist.hasHydrated()
  );

  const storedCartId = useCartStore((s) => s.cartId);
  const setStoredCartId = useCartStore((s) => s.setCartId);
  const resetCartStore = useCartStore((s) => s.resetCart);

  const [cartId, setCartId] = useState(() => useCartStore.getState().cartId || null);

  const { data: cartData } = useQuery(GET_CART, {
    variables: { cartId },
    skip: !cartId,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (cartData?.cart) {
      setCart(cartData.cart);
    }
  }, [cartData]);

  useEffect(() => {
    const unsubscribeHydration = useCartStore.persist.onFinishHydration(() => {
      setHasHydratedCartStore(true);
    });

    setHasHydratedCartStore(useCartStore.persist.hasHydrated());

    return unsubscribeHydration;
  }, []);

  const [createCartMutation,
  {
    data: createCartData,
    loading: createCartLoading,
    error: createCartError
  }] = useMutation(createCart);

  useCheckoutEffect(createCartData, 'cartCreate', (newCart) => {
    setCart(newCart);
    setCartId(newCart.id);
    setStoredCartId(newCart.id);
  });

  if (createCartError) throw createCartError;

  useEffect(() => {
    if (storedCartId) {
      setCartId(storedCartId);
    }
  }, [storedCartId]);

  useEffect(() => {
    if (!hasHydratedCartStore) {
      return;
    }

    const hydratedCartId = useCartStore.getState().cartId;
    if (hydratedCartId) {
      setCartId(hydratedCartId);
      return;
    }

    const variables = { input: {} };
    createCartMutation({ variables }).catch(
      err => {
        console.error('Create cart error:', err);
      }
    );
  }, [createCartMutation, hasHydratedCartStore]);

  const resetToNewCart = () => {
    client.clearStore();
    resetCartStore();
  };

  return (
    <CartContext.Provider value={{cartState:[cart, setCart], createCartLoading, resetToNewCart}}>
      {props.children}
    </CartContext.Provider>
  );
}

const CheckoutProvider = CartProvider;

export { CartContext, CartProvider, CheckoutContext, CheckoutProvider };
